import { useRouteContext } from '@tanstack/react-router';
import { buildAlternates, resolveSlugSeo, Seo } from '@/routes/-components/seo';
import { wwwLocales } from './locales';

/**
 * Every redesign page's head, in one place: title, description, canonical and the `hreflang` set.
 *
 * A wrapper rather than five direct `<Seo>` calls because the three things that must not drift between
 * pages are exactly the three it fixes — the locale set advertised, the site name, and the shape of the
 * canonical. A page supplies only what is genuinely its own: its copy and its slugs.
 *
 * `slugs` comes from the page's co-located `.meta.ts`, which is the SAME object the router's rewrite
 * and the language switcher read. So a canonical URL cannot claim a path the rewrite would not serve.
 */
export function PageSeo({
	slugs,
	title,
	description,
	noindex,
	jsonLd,
}: {
	/** The page's `slugs` export. Empty for the homepage, which lives at the locale root. */
	slugs: Record<string, string>;
	title: string;
	description: string;
	/** For a page that exists but should not be indexed yet — a placeholder, or the 404. */
	noindex?: boolean;
	/**
	 * Schema.org blocks for this page (FAQPage, Review, …). Passed straight through to `<Seo>`, which
	 * owns hoisting and de-duplicating them in the head. Pages that describe the same content twice —
	 * on-page and in markup — should build the two from one array so they cannot drift.
	 */
	jsonLd?: Record<string, unknown>[];
}) {
	const { baseUrl, locale, hasLocalePrefix } = useRouteContext({ from: '__root__' });

	// The homepage has no slug: it IS the locale root, so its alternates are path-based and its
	// canonical is just the prefix. `resolveSlugSeo` would resolve an empty map to `undefined` and
	// write `/es/undefined`.
	const isHome = Object.keys(slugs).length === 0;
	const { canonicalUrl, alternates } = isHome
		? {
				canonicalUrl: hasLocalePrefix ? `${baseUrl}/${locale}` : baseUrl,
				alternates: buildAlternates(baseUrl, '', wwwLocales),
			}
		: resolveSlugSeo(hasLocalePrefix, baseUrl, locale, wwwLocales, slugs);

	return (
		<Seo
			title={title}
			description={description}
			siteName="Worldcoo"
			canonicalUrl={canonicalUrl}
			locale={locale}
			alternateLocales={[...wwwLocales]}
			alternates={alternates}
			noindex={noindex}
			jsonLd={jsonLd}
		/>
	);
}
